import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  Fragment
} from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { fade } from '@material-ui/core/styles';
import { v4 as uuidv4 } from 'uuid';
import { generateKeyBetween } from 'fractional-indexing';
import {
  DndContext,
  useDroppable,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  Collision,
  CollisionDetection,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  MeasuringStrategy
} from '@dnd-kit/core';
import {
  SortableContext,
  SortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { SBase, SText, SButton, SDropdownMenu } from '@setvi/shared/components';
import { useDialog } from '@setvi/shared/providers';
import {
  QuoteItemDTO,
  QuoteSectionDTO,
  QuoteProductDTO
} from '@setvi/shared/services/react-query';
import type {
  ReorderMove,
  NewSectionSpec
} from '../../hooks/use-reorder-items';
import { SectionDialog } from '../add-section-dialog';
import { DraggableItem } from './draggable-item';
import { DraggableSectionItem } from './draggable-section';
import { DraggableProductItem } from './draggable-product-item';

const useStyles = makeStyles(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    height: '100%',
    overflowY: 'auto'
  },
  itemsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flex: 1
  },
  dragOverlay: {
    opacity: 0.9,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
  },
  // Action bar shown while one or more products are selected. Sticks to the top
  // of the scrolling list so it stays visible at all times. The background must
  // be OPAQUE — a translucent tint would let items scrolling behind it bleed
  // through — so the light-blue tint is layered over a solid white base.
  selectionBar: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px 10px 16px',
    borderRadius: '12px',
    background: `linear-gradient(${fade(
      theme.palette.primary.main,
      0.08
    )}, ${fade(theme.palette.primary.main, 0.08)}), #FFFFFF`,
    border: `1px solid ${fade(theme.palette.primary.main, 0.24)}`
  },
  selectionCount: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginRight: 'auto',
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.primary.dark
  },
  moveToLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#000000'
  },
  selectionCountBadge: {
    minWidth: '24px',
    width: '24px',
    height: '24px',
    padding: 0,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText || '#FFFFFF',
    fontSize: '13px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  countBadge: {
    position: 'absolute',
    top: '-8px',
    right: '-8px',
    minWidth: '22px',
    height: '22px',
    padding: '0 6px',
    borderRadius: '999px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText || '#FFFFFF',
    fontSize: '12px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
  },
  clearButton: {
    color: theme.palette.text.secondary
  },
  overlayWrapper: {
    position: 'relative'
  },
  // Root-level insertion marker for placing an item at a precise top-level spot.
  // The rows stay put during a drag (the sortable strategy is a no-op), so the
  // marker must NOT take layout space — it is a zero-height slot holding an
  // absolutely-positioned dashed line that only appears at the hovered gap. It
  // cancels the container's 12px flex gap with a negative margin so it adds no
  // spacing. Rendered only while dragging.
  rootGap: {
    position: 'relative',
    height: 0,
    marginTop: '-12px'
  },
  // The dashed insertion line, centered in the 12px gap between two rows. Hidden
  // until its gap is the drop target; shown as an overlay so it never shifts the
  // surrounding rows.
  rootGapLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '6px',
    height: 0,
    borderTop: `2px dashed ${theme.palette.primary.main}`,
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 120ms ease'
  },
  rootGapLineActive: {
    opacity: 1
  }
}));

// Droppable id prefix for a section's children area, so a product can be
// dropped into an empty/expanded section (not just onto another child).
export const SECTION_DROP_PREFIX = 'section-drop:';
// Droppable id prefix for a root-level insertion gap. The suffix is the root
// index the item should be inserted at, so a product pulled out of a section
// can land precisely before/between/after sections and root products.
const ROOT_GAP_PREFIX = 'root-gap:';
const ROOT = 'root';
const NEW_SECTION_DIALOG_ID = 'reorder-new-section';

interface RootGapDropZoneProps {
  index: number;
}

/**
 * A zero-height droppable slot between two root-level items. Its hit target
 * never resizes; only an overlaid dashed line appears when hovered, so an item
 * dropped here lands at exactly this root index without shifting any rows.
 */
const RootGapDropZone = ({ index }: RootGapDropZoneProps) => {
  const classes = useStyles();
  const { setNodeRef, isOver } = useDroppable({
    id: `${ROOT_GAP_PREFIX}${index}`
  });
  return (
    <div ref={setNodeRef} className={classes.rootGap}>
      <span
        className={`${classes.rootGapLine} ${
          isOver ? classes.rootGapLineActive : ''
        }`}
      />
    </div>
  );
};

/**
 * True when the dragged item sits past the vertical midpoint of the `over`
 * target — i.e. the drop should land AFTER that item rather than before it.
 * This is what lets a product be dropped below the LAST child of a section:
 * once a section's children fill its area, closestCorners always resolves to
 * the last child, so without this check the drop can only ever land before it.
 */
const isAfterOverItem = (event: DragOverEvent | DragEndEvent): boolean => {
  const activeRect = event.active.rect.current.translated;
  const overRect = event.over?.rect;
  if (!activeRect || !overRect) return false;
  const activeCenter = activeRect.top + activeRect.height / 2;
  return activeCenter > overRect.top + overRect.height / 2;
};

/**
 * A sorting strategy that never displaces items: the dragged row stays faded in
 * its original spot and the other rows do not slide to open a gap. The drop
 * position is shown instead by the dashed insertion markers, and the actual
 * reorder happens on drop. Shared by the root list and every section's children.
 */
export const noReflowStrategy: SortingStrategy = () => null;

/**
 * Collision strategy for the reorder tree, measuring VERTICAL corner distance
 * only. Section children are indented 32px, so with the stock closestCorners
 * (full 2D distance) a child's drag rect is permanently offset from the
 * full-width root insertion markers and can never reach them — you couldn't
 * pull a product out of a section. Comparing vertical distance only is
 * indent-agnostic and correct for a vertical list. (Rows never move mid-drag,
 * so the measured geometry is stable and the resolved target doesn't oscillate.)
 */
const treeCollisionDetection: CollisionDetection = args => {
  const { collisionRect, droppableContainers, droppableRects } = args;
  const activeTop = collisionRect.top;
  const activeBottom = collisionRect.top + collisionRect.height;

  const collisions: Collision[] = [];
  droppableContainers.forEach(container => {
    const rect = droppableRects.get(container.id);
    if (rect) {
      // Two corners share the rect's top, two share its bottom — vertical only.
      const value =
        (Math.abs(activeTop - rect.top) +
          Math.abs(activeBottom - (rect.top + rect.height))) /
        2;
      collisions.push({
        id: container.id,
        data: { droppableContainer: container, value }
      });
    }
  });

  return collisions.sort((a, b) => (a.data?.value ?? 0) - (b.data?.value ?? 0));
};

const getId = (item: QuoteItemDTO): string => item.id || (item as any)._id;
const getPos = (item: QuoteItemDTO): string | null =>
  (item as any).position || null;

const withPos = <T extends QuoteItemDTO>(item: T, position: string): T =>
  ({ ...item, position }) as any;

/**
 * Assign sequential fractional positions to all items in the array.
 * Ensures every item has a unique, correctly ordered position.
 */
export const assignPositions = <T extends QuoteItemDTO>(items: T[]): T[] => {
  let lastPos: string | null = null;
  return items.map(item => {
    const pos = generateKeyBetween(lastPos, null);
    lastPos = pos;
    return withPos(item, pos);
  });
};

// ---------------------------------------------------------------------------
// Container helpers — a "container" is either the root list (ROOT) or a
// section's children list (keyed by section id). Products can move between
// containers; sections only reorder within ROOT.
// ---------------------------------------------------------------------------

/** Ordered item ids in a container. */
const getContainerItemIds = (
  items: QuoteItemDTO[],
  containerId: string
): string[] => {
  if (containerId === ROOT) return items.map(getId);
  const section = items.find(
    it => it.type === 'section' && getId(it) === containerId
  ) as QuoteSectionDTO | undefined;
  return (section?.children || []).map(getId);
};

/**
 * Resolve the container an id belongs to. `id` may be a droppable id
 * (`section-drop:<sectionId>`), the ROOT list, a root item, or a section child.
 */
const findContainer = (items: QuoteItemDTO[], id: string): string | null => {
  if (id.startsWith(SECTION_DROP_PREFIX))
    return id.slice(SECTION_DROP_PREFIX.length);
  if (id === ROOT) return ROOT;
  if (items.some(it => getId(it) === id)) return ROOT;
  for (const it of items) {
    if (it.type === 'section' && (it.children || []).some(c => getId(c) === id))
      return getId(it);
  }
  return null;
};

/** Find an item anywhere at the root/section-child level. */
const getItemById = (
  items: QuoteItemDTO[],
  id: string
): QuoteItemDTO | null => {
  for (const it of items) {
    if (getId(it) === id) return it;
    if (it.type === 'section') {
      const child = (it.children || []).find(c => getId(c) === id);
      if (child) return child;
    }
  }
  return null;
};

/** Remove an item (root item or section child) and return [item, remaining]. */
const removeItem = (
  items: QuoteItemDTO[],
  id: string
): [QuoteItemDTO | null, QuoteItemDTO[]] => {
  const rootIdx = items.findIndex(it => getId(it) === id);
  if (rootIdx !== -1) {
    return [items[rootIdx], items.filter((_, i) => i !== rootIdx)];
  }
  let found: QuoteItemDTO | null = null;
  const next = items.map(it => {
    if (it.type === 'section') {
      const children = it.children || [];
      const idx = children.findIndex(c => getId(c) === id);
      if (idx !== -1) {
        found = children[idx];
        return {
          ...it,
          children: children.filter((_, i) => i !== idx)
        } as QuoteSectionDTO;
      }
    }
    return it;
  });
  return [found, next];
};

/**
 * Collect items (root products or section children) whose id is in `ids`,
 * preserving their current tree order. Sections themselves are never selectable
 * so only products are returned.
 */
const collectItemsInOrder = (
  items: QuoteItemDTO[],
  ids: Set<string>
): QuoteItemDTO[] => {
  const result: QuoteItemDTO[] = [];
  items.forEach(it => {
    if (it.type !== 'section' && ids.has(getId(it))) result.push(it);
    if (it.type === 'section') {
      (it.children || []).forEach(c => {
        if (ids.has(getId(c))) result.push(c);
      });
    }
  });
  return result;
};

/** Remove every item whose id is in `ids` from root and section children. */
const removeItems = (items: QuoteItemDTO[], ids: Set<string>): QuoteItemDTO[] =>
  items
    .filter(it => !ids.has(getId(it)))
    .map(it =>
      it.type === 'section'
        ? ({
            ...it,
            children: (it.children || []).filter(c => !ids.has(getId(c)))
          } as QuoteSectionDTO)
        : it
    );

/** The set of containers a group of ids currently lives in. */
const getSourceContainers = (
  items: QuoteItemDTO[],
  ids: Set<string>
): Set<string> => {
  const containers = new Set<string>();
  ids.forEach(id => {
    const c = findContainer(items, id);
    if (c) containers.add(c);
  });
  return containers;
};

/** Insert an item into a container at a given index. */
const insertItem = (
  items: QuoteItemDTO[],
  containerId: string,
  item: QuoteItemDTO,
  index: number
): QuoteItemDTO[] => {
  if (containerId === ROOT) {
    const next = [...items];
    next.splice(index, 0, item);
    return next;
  }
  return items.map(it => {
    if (it.type === 'section' && getId(it) === containerId) {
      const children = [...(it.children || [])];
      children.splice(index, 0, item as QuoteProductDTO);
      return { ...it, children } as QuoteSectionDTO;
    }
    return it;
  });
};

/** Reorder items within a single container (root or a section's children). */
const reorderWithin = (
  items: QuoteItemDTO[],
  containerId: string,
  oldIndex: number,
  newIndex: number
): QuoteItemDTO[] => {
  if (containerId === ROOT) return arrayMove(items, oldIndex, newIndex);
  return items.map(it =>
    it.type === 'section' && getId(it) === containerId
      ? ({
          ...it,
          children: arrayMove(it.children || [], oldIndex, newIndex)
        } as QuoteSectionDTO)
      : it
  );
};

/** Reassign sequential positions to a single container's items. */
const assignPositionsIn = (
  items: QuoteItemDTO[],
  containerId: string
): QuoteItemDTO[] => {
  if (containerId === ROOT) return assignPositions(items);
  return items.map(it =>
    it.type === 'section' && getId(it) === containerId
      ? ({
          ...it,
          children: assignPositions((it.children || []) as QuoteProductDTO[])
        } as QuoteSectionDTO)
      : it
  );
};

/** Replace a product's children (options) wherever the product lives. */
const setProductChildren = (
  items: QuoteItemDTO[],
  productId: string,
  children: QuoteProductDTO[]
): QuoteItemDTO[] =>
  items.map(it => {
    if (it.type === 'product' && getId(it) === productId) {
      return { ...it, children } as QuoteProductDTO;
    }
    if (it.type === 'section') {
      return {
        ...it,
        children: (it.children || []).map(child =>
          getId(child) === productId ? { ...child, children } : child
        )
      } as QuoteSectionDTO;
    }
    return it;
  });

/**
 * Collect items whose position OR parent container changed vs. the original,
 * across root, section children, and product options. A parent change (moving
 * a product to another section) is reported even if the position string didn't
 * change, so cross-section moves are always persisted.
 */
const buildMoves = (
  current: QuoteItemDTO[],
  original: QuoteItemDTO[]
): ReorderMove[] => {
  const orig = new Map<
    string,
    { position: string | null; parentId: string | null }
  >();
  const index = (items: QuoteItemDTO[], parentId: string | null) => {
    items.forEach(it => {
      orig.set(getId(it), { position: getPos(it), parentId });
      if (it.type === 'section') index(it.children || [], getId(it));
      if (it.type === 'product' && (it as QuoteProductDTO).children?.length)
        index((it as QuoteProductDTO).children as QuoteItemDTO[], getId(it));
    });
  };
  index(original, null);

  const moves: ReorderMove[] = [];
  const walk = (items: QuoteItemDTO[], parentId: string | null) => {
    items.forEach(it => {
      const id = getId(it);
      const pos = getPos(it);
      const o = orig.get(id);
      if (pos && (!o || o.position !== pos || o.parentId !== parentId)) {
        moves.push({ itemId: id, position: pos, targetSectionId: parentId });
      }
      if (it.type === 'section') walk(it.children || [], id);
      if (it.type === 'product' && (it as QuoteProductDTO).children?.length)
        walk((it as QuoteProductDTO).children as QuoteItemDTO[], id);
    });
  };
  walk(current, null);
  return moves;
};

interface ReorderItemsDialogProps {
  items: QuoteItemDTO[];
  onClose: () => void;
  onSave: (
    moves: ReorderMove[],
    newSections?: NewSectionSpec[]
  ) => Promise<any>;
  isSaving?: boolean;
}

/**
 * Dialog for reordering products and sections in a quote. A single DnD context
 * spans the root list and every section's children, so products can be dragged
 * within a container, between sections, or in/out of the root. Sections reorder
 * within the root; product options reorder within their product.
 */
export const ReorderItemsDialog = ({
  items,
  onClose,
  onSave,
  isSaving = false
}: ReorderItemsDialogProps) => {
  const classes = useStyles();

  const [localItems, setLocalItems] = useState<QuoteItemDTO[]>(items);
  const [activeId, setActiveId] = useState<string | null>(null);
  // Section whose children area the drag is currently hovering (over the empty
  // drop zone OR over one of its children), so it can be highlighted.
  const [overSectionId, setOverSectionId] = useState<string | null>(null);
  // Raw id currently under the drag (a child id or a section drop zone id),
  // used to draw the dashed insertion indicator at the exact drop position.
  const [overId, setOverId] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set()
  );
  // Ids of products ticked for a bulk move.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Temp ids of sections created inside the dialog (not yet on the server).
  const [newSectionIds, setNewSectionIds] = useState<Set<string>>(new Set());

  // Scrollable list container + the section a just-completed bulk move landed
  // in. After moving products into a section (new or existing) the products
  // vanish from their old spot, so we scroll their destination into view.
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollToSectionId, setScrollToSectionId] = useState<string | null>(
    null
  );

  // Once localItems reflects the move, bring the target section into view.
  useEffect(() => {
    if (!scrollToSectionId) return;
    const el = listRef.current?.querySelector(
      `[data-section-id="${scrollToSectionId}"]`
    );
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    setScrollToSectionId(null);
  }, [scrollToSectionId, localItems]);

  const { openDialog, closeDialog } = useDialog();

  const itemIds = useMemo(() => localItems.map(getId), [localItems]);

  // Root-level sections that can receive a bulk move.
  const sectionOptions = useMemo(
    () =>
      localItems
        .filter(it => it.type === 'section')
        .map(it => ({
          id: getId(it),
          name: (it as QuoteSectionDTO).name || 'Unnamed Section'
        })),
    [localItems]
  );

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Drag that carries the whole selection (dragging one of the ticked rows).
  const isGroupDrag =
    !!activeId && selectedIds.has(activeId) && selectedIds.size > 1;

  const activeItem = useMemo(
    () => (activeId ? getItemById(localItems, activeId) : null),
    [activeId, localItems]
  );

  // Show root insertion markers whenever anything is being dragged, so it can be
  // placed precisely before/between/after sections and root products. Sections
  // use them too — since rows no longer slide to reveal the drop spot, the
  // markers are the only cue for reordering sections among the root list.
  const showRootGaps = !!activeId;

  const renderDragOverlay = useCallback(() => {
    if (!activeItem) return null;

    if (activeItem.type === 'section') {
      return (
        <DraggableSectionItem
          section={activeItem as QuoteSectionDTO}
          isExpanded={false}
          onToggle={() => {}}
          isDragOverlay
        />
      );
    }

    const product = activeItem as QuoteProductDTO;
    if (product.children?.length > 0) {
      return (
        <DraggableProductItem
          product={product}
          onChildrenReorder={() => {}}
          isDragOverlay
        />
      );
    }

    return <DraggableItem item={product} isDragOverlay />;
  }, [activeItem]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  // Expand every section on drag start so their children lists become visible
  // drop targets — otherwise a product couldn't be dropped into a collapsed one.
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      setActiveId(String(event.active.id));
      const sectionIds = localItems
        .filter(it => it.type === 'section')
        .map(getId);
      if (sectionIds.length) setExpandedSections(new Set(sectionIds));
    },
    [localItems]
  );

  // Track which section (if any) the drag is over so it stays highlighted even
  // once it has children — the child would otherwise become the collision
  // target and the section's own `isOver` would never fire.
  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over) {
        setOverSectionId(null);
        setOverId(null);
        return;
      }
      // Don't highlight a section drop target while dragging a section.
      if (getItemById(localItems, String(active.id))?.type === 'section') {
        setOverSectionId(null);
        setOverId(null);
        return;
      }
      const container = findContainer(localItems, String(over.id));
      setOverSectionId(container && container !== ROOT ? container : null);

      // When hovering below the last child of a section, target the section's
      // end drop zone instead of the last child, so the end-insertion indicator
      // shows (and the drop appends). closestCorners can't reach the end zone on
      // its own once the children fill the section.
      let resolvedOverId = String(over.id);
      if (container && container !== ROOT && isAfterOverItem(event)) {
        const childIds = getContainerItemIds(localItems, container);
        if (
          childIds.length &&
          childIds[childIds.length - 1] === String(over.id)
        )
          resolvedOverId = `${SECTION_DROP_PREFIX}${container}`;
      }
      setOverId(resolvedOverId);
    },
    [localItems]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);
      setOverSectionId(null);
      setOverId(null);
      if (!over) return;

      const activeIdStr = String(active.id);
      const overIdStr = String(over.id);
      if (activeIdStr === overIdStr) return;

      // A root insertion gap targets the ROOT container at an explicit index.
      const rootGapIndex = overIdStr.startsWith(ROOT_GAP_PREFIX)
        ? parseInt(overIdStr.slice(ROOT_GAP_PREFIX.length), 10)
        : null;

      // Group drag: dragging one of several selected rows moves them all as a
      // contiguous block into the drop target's container.
      if (selectedIds.has(activeIdStr) && selectedIds.size > 1) {
        setLocalItems(prev => {
          const overContainer =
            rootGapIndex != null ? ROOT : findContainer(prev, overIdStr);
          if (!overContainer) return prev;

          const moving = collectItemsInOrder(prev, selectedIds);
          if (!moving.length) return prev;

          const sources = getSourceContainers(prev, selectedIds);
          const without = removeItems(prev, selectedIds);

          const overIds = getContainerItemIds(without, overContainer);
          let index: number;
          if (rootGapIndex != null) {
            // The gap index was measured against the full root; subtract any
            // selected root items removed from before it so it still points at
            // the intended slot.
            const prevRootIds = getContainerItemIds(prev, ROOT);
            const removedBefore = prevRootIds
              .slice(0, rootGapIndex)
              .filter(id => selectedIds.has(id)).length;
            index = Math.min(
              Math.max(rootGapIndex - removedBefore, 0),
              overIds.length
            );
          } else {
            index = overIds.indexOf(overIdStr);
            if (index === -1) index = overIds.length;
            else if (isAfterOverItem(event)) index += 1;
          }

          let result = without;
          moving.forEach((item, i) => {
            result = insertItem(result, overContainer, item, index + i);
          });

          const affected = new Set<string>([
            overContainer,
            ...Array.from(sources)
          ]);
          affected.forEach(c => {
            result = assignPositionsIn(result, c);
          });
          return result;
        });
        clearSelection();
        return;
      }

      setLocalItems(prev => {
        const activeContainer = findContainer(prev, activeIdStr);
        const overContainer =
          rootGapIndex != null ? ROOT : findContainer(prev, overIdStr);
        if (!activeContainer || !overContainer) return prev;

        const moving = getItemById(prev, activeIdStr);

        // Dropped on a root insertion gap: place the item (product OR section)
        // at that explicit root index. Handled before the reorder branches
        // because when the item already lives at the root this is still a
        // ROOT→ROOT move (the same-container branch can't resolve a gap id).
        if (rootGapIndex != null) {
          const activeRootIdx = getContainerItemIds(prev, ROOT).indexOf(
            activeIdStr
          );
          const [item, without] = removeItem(prev, activeIdStr);
          if (!item) return prev;
          const rootIds = getContainerItemIds(without, ROOT);
          // Removing the active item from before the gap shifts the target left.
          let index = rootGapIndex;
          if (activeRootIdx !== -1 && activeRootIdx < rootGapIndex) index -= 1;
          index = Math.min(Math.max(index, 0), rootIds.length);

          let result = insertItem(without, ROOT, item, index);
          result = assignPositionsIn(result, ROOT);
          if (activeContainer !== ROOT)
            result = assignPositionsIn(result, activeContainer);
          return result;
        }

        // Sections only ever reorder within the root list.
        if (moving?.type === 'section') {
          const rootIds = getContainerItemIds(prev, ROOT);
          const overRootId = overContainer === ROOT ? overIdStr : overContainer;
          const oldIndex = rootIds.indexOf(activeIdStr);
          const newIndex = rootIds.indexOf(overRootId);
          if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex)
            return prev;
          return assignPositionsIn(
            reorderWithin(prev, ROOT, oldIndex, newIndex),
            ROOT
          );
        }

        // Reorder within the same container.
        if (activeContainer === overContainer) {
          const ids = getContainerItemIds(prev, activeContainer);
          const oldIndex = ids.indexOf(activeIdStr);
          const newIndex = ids.indexOf(overIdStr);
          if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex)
            return prev;
          return assignPositionsIn(
            reorderWithin(prev, activeContainer, oldIndex, newIndex),
            activeContainer
          );
        }

        // Move a product across containers (into/out of a section, or between
        // sections). Insert at the over target's index, or the end when dropped
        // on a section's empty area.
        const [item, without] = removeItem(prev, activeIdStr);
        if (!item) return prev;
        const overIds = getContainerItemIds(without, overContainer);
        let index = overIds.indexOf(overIdStr);
        if (index === -1) index = overIds.length;
        else if (isAfterOverItem(event)) index += 1;

        let result = insertItem(without, overContainer, item, index);
        result = assignPositionsIn(result, overContainer);
        result = assignPositionsIn(result, activeContainer);
        return result;
      });
    },
    [selectedIds, clearSelection]
  );

  const handleOptionsReorder = useCallback(
    (productId: string, children: QuoteProductDTO[]) => {
      setLocalItems(prev => setProductChildren(prev, productId, children));
    },
    []
  );

  // Move every selected product to the end of `containerId` (ROOT or a section).
  const moveSelectedToContainer = useCallback(
    (containerId: string) => {
      setLocalItems(prev => {
        const moving = collectItemsInOrder(prev, selectedIds);
        if (!moving.length) return prev;

        const sources = getSourceContainers(prev, selectedIds);
        const without = removeItems(prev, selectedIds);
        const index = getContainerItemIds(without, containerId).length;

        let result = without;
        moving.forEach((item, i) => {
          result = insertItem(result, containerId, item, index + i);
        });

        const affected = new Set<string>([containerId, ...Array.from(sources)]);
        affected.forEach(c => {
          result = assignPositionsIn(result, c);
        });
        return result;
      });
      // Moving to an actual section relocates the rows; scroll it into view so
      // the user can see where they went (moving to top level leaves them in
      // the root list, no scroll needed).
      if (containerId !== ROOT) {
        setExpandedSections(prev => new Set(prev).add(containerId));
        setScrollToSectionId(containerId);
      }
      clearSelection();
    },
    [selectedIds, clearSelection]
  );

  // Create a new (client-only) section at the end of the root and move every
  // selected product into it. The section is persisted for real on save.
  const confirmNewSection = useCallback(
    ({ name, description }: { name: string; description?: string }) => {
      const tempId = uuidv4();

      setLocalItems(prev => {
        const moving = collectItemsInOrder(prev, selectedIds);
        if (!moving.length) return prev;

        const sources = getSourceContainers(prev, selectedIds);
        const without = removeItems(prev, selectedIds);

        const newSection = {
          id: tempId,
          _id: tempId,
          type: 'section',
          name,
          note: description || null,
          children: moving as QuoteProductDTO[]
        } as unknown as QuoteSectionDTO;

        let result: QuoteItemDTO[] = [...without, newSection];
        result = assignPositionsIn(result, ROOT);
        result = assignPositionsIn(result, tempId);
        sources.forEach(c => {
          if (c !== ROOT) result = assignPositionsIn(result, c);
        });
        return result;
      });

      setNewSectionIds(prev => new Set(prev).add(tempId));
      setExpandedSections(prev => new Set(prev).add(tempId));
      // The new section is appended at the bottom of the root list; scroll it
      // into view so the moved products are visible in their new home.
      setScrollToSectionId(tempId);
      clearSelection();
    },
    [selectedIds, clearSelection]
  );

  // Open the app's standard section dialog as a top-level modal (not nested
  // inside this dialog, which would clip it).
  const openNewSectionDialog = useCallback(() => {
    openDialog(
      <SectionDialog
        onClose={() => closeDialog(NEW_SECTION_DIALOG_ID)}
        addSection={confirmNewSection}
      />,
      { id: NEW_SECTION_DIALOG_ID }
    );
  }, [openDialog, closeDialog, confirmNewSection]);

  const handleSave = useCallback(async () => {
    const allMoves = buildMoves(localItems, items);

    // Sections created in the dialog don't exist server-side yet — send them as
    // specs (so they get created first) and drop their placeholder "moves".
    const newSections: NewSectionSpec[] = localItems
      .filter(it => it.type === 'section' && newSectionIds.has(getId(it)))
      .map(it => ({
        tempId: getId(it),
        name: (it as QuoteSectionDTO).name || 'New Section',
        note: (it as QuoteSectionDTO).note || '',
        position: getPos(it) || generateKeyBetween(null, null)
      }));

    const moves = allMoves.filter(m => !newSectionIds.has(m.itemId));

    onClose();
    if (moves.length > 0 || newSections.length > 0)
      await onSave(moves, newSections);
  }, [localItems, items, newSectionIds, onSave, onClose]);

  const hasChanges = useMemo(
    () => buildMoves(localItems, items).length > 0 || newSectionIds.size > 0,
    [localItems, items, newSectionIds]
  );

  return (
    <SBase
      width={800}
      height={850}
      style={{ backgroundColor: '#FFFFFF' }}
      header={{
        title: 'Reorder',
        description:
          'Drag sections or items to set their order. Drag a product into a section to move it there.',
        onClose
      }}
      footer={{
        onConfirm: handleSave,
        onClose,
        confirmLabel: 'Save Changes',
        closeLabel: 'Cancel',
        disableConfirm: !hasChanges || isSaving,
        confirmLoading: isSaving
      }}>
      <div className={classes.content} ref={listRef}>
        {selectedIds.size > 0 && (
          <Box className={classes.selectionBar}>
            <span className={classes.selectionCount}>
              <span className={classes.selectionCountBadge}>
                {selectedIds.size}
              </span>
              Selected
            </span>
            <span className={classes.moveToLabel}>Move to:</span>
            <SDropdownMenu
              newDesign
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              menuItems={[
                {
                  text: 'Top level (no section)',
                  color: '#333D47',
                  onClick: () => moveSelectedToContainer(ROOT)
                },
                ...sectionOptions.map(section => ({
                  text: section.name,
                  color: '#333D47',
                  onClick: () => moveSelectedToContainer(section.id)
                }))
              ]}>
              <SButton size="small" variant="outlined">
                Existing Section
              </SButton>
            </SDropdownMenu>
            <SButton
              size="small"
              variant="contained"
              onClick={openNewSectionDialog}>
              New Section
            </SButton>
            <SButton
              size="small"
              variant="text"
              color="default"
              className={classes.clearButton}
              onClick={clearSelection}>
              Clear
            </SButton>
          </Box>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={treeCollisionDetection}
          // Re-measure droppables continuously. Sections expand on drag start
          // and every drop reshuffles the list, so the default WhileDragging
          // rects go stale and the section drop-highlight stops firing after
          // the first drag.
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragCancel={() => {
            setActiveId(null);
            setOverSectionId(null);
            setOverId(null);
          }}
          onDragEnd={handleDragEnd}>
          <SortableContext items={itemIds} strategy={noReflowStrategy}>
            <Box className={classes.itemsContainer}>
              {localItems.map((item, i) => {
                const itemId = getId(item);

                let rendered;
                if (item.type === 'section') {
                  rendered = (
                    <DraggableSectionItem
                      section={item as QuoteSectionDTO}
                      isExpanded={expandedSections.has(itemId)}
                      onToggle={toggleSection}
                      onProductOptionsReorder={handleOptionsReorder}
                      selectedIds={selectedIds}
                      onToggleSelect={toggleSelect}
                      isDropTarget={overSectionId === itemId}
                      overId={overSectionId === itemId ? overId : null}
                      activeId={activeId}
                    />
                  );
                } else {
                  const product = item as QuoteProductDTO;
                  rendered =
                    product.children && product.children.length > 0 ? (
                      <DraggableProductItem
                        product={product}
                        onChildrenReorder={handleOptionsReorder}
                        selectable
                        selected={selectedIds.has(itemId)}
                        onToggleSelect={toggleSelect}
                      />
                    ) : (
                      <DraggableItem
                        item={product}
                        selectable
                        selected={selectedIds.has(itemId)}
                        onToggleSelect={toggleSelect}
                      />
                    );
                }

                return (
                  <Fragment key={itemId}>
                    {showRootGaps && <RootGapDropZone index={i} />}
                    {rendered}
                  </Fragment>
                );
              })}
              {showRootGaps && <RootGapDropZone index={localItems.length} />}
            </Box>
          </SortableContext>

          <DragOverlay>
            {activeItem ? (
              <Box
                className={`${classes.dragOverlay} ${classes.overlayWrapper}`}>
                {renderDragOverlay()}
                {isGroupDrag && (
                  <span className={classes.countBadge}>{selectedIds.size}</span>
                )}
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>

        {localItems.length === 0 && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flex={1}>
            <SText
              title="No items to reorder"
              style={{ color: '#5D7083', textAlign: 'center' }}
            />
          </Box>
        )}
      </div>
    </SBase>
  );
};
